import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useTable, useGlobalFilter, useSortBy } from 'react-table'
import Search from '../../Assets/Images/Departamentos/search.png'
import './table.css'
import {
  Filter,
  EyeSlash,
  Eye,
  ArrowDownUp,
  ArrowDown,
  ArrowUp,
} from 'react-bootstrap-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePen, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { VAPermissions } from 'Utils/constants'
import useValidatePermission from 'Hooks/useValidatePermission'
import { t } from 'i18next'
import { CustomSwitch } from 'Components/Switch/CustomSwitch.jsx'
import { useFreeTaxes } from 'state/context/CotizationTaxesContext'
import { Formatter } from 'Utils/Formatter'
import { useSelector } from 'react-redux'

const getHiddenColumnKey = columns => [...columns].sort().join(',')

const getToggleableColumns = columns =>
  columns
    .filter(column => column.hideEnabled)
    .map(column => ({ ColumnName: column.Header, Status: column.isVisible }))

function FilteringTable({
  data,
  prodWithNoCost,
  columns,
  showMargin,
  isProductPage,
  topButtons,
  hasButtons,
  hideFooter,
  isInventory,
  setHiddenColumns,
  hiddenColumns,
  hiddenColumnDiscount,
  handleCartEdit,
  handleCartPopulation,
  generateSalesOrder,
  isPriceNotApproved,
  isSaleOrderGenerated,
  showDiscountOption,
  visibleIcons,
  isDispatchGenerated,
  enableSorting = false,
  handleSortChange,
  isDetailQuote,
}) {
  const isUserAllowed = useValidatePermission(VAPermissions.createOrder)
  const currentPath = location.pathname
  const { freeTaxes, setFreeTaxes, setIsChanged } = useFreeTaxes()
  const validateSalesOrder =
    isSaleOrderGenerated && !isPriceNotApproved && isUserAllowed
  const useSelectorDetalle = useSelector(state => state.cotizacion.QuoteHeader)

  const channelRef = useRef(null)
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('customer_display')
  }
  const channel = channelRef.current

  const handleSwitchChange = e => {
    setFreeTaxes(e.target.checked)
    setIsChanged(true)
  }

  const seeWarehouseMPD1 = useValidatePermission(
    VAPermissions.seeWareHouse_D1_MP
  )

  const filteredData = useMemo(() => {
    if (!seeWarehouseMPD1) {
      return data?.filter(
        item =>
          !item?.['Código']?.includes('D1') && !item?.['Código']?.includes('MP')
      )
    } else {
      return data
    }
  }, [data, seeWarehouseMPD1])

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        hiddenColumns,
      },
      disableSortRemove: true,
      manualSortBy: Boolean(handleSortChange),
    },
    useGlobalFilter,
    ...(enableSorting ? [useSortBy] : [])
  )
  const inventoryFilter = useRef(null)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    allColumns,
    state,
    setGlobalFilter,
    setHiddenColumns: setTableHiddenColumns,
  } = tableInstance

  // TABLE LOGIC
  const [showInventoryFilters, setShowInventoryFilters] = useState(false)

  // Stable key to track visibility changes.
  const allColumnsVisibilityKey = allColumns
    .filter(col => col.hideEnabled)
    .map(col => `${col.id}:${col.isVisible}`)
    .join(',')

  const toggleableColumns = useMemo(
    () => getToggleableColumns(allColumns),
    [allColumnsVisibilityKey]
  )
  const hiddenColumnKey = getHiddenColumnKey(hiddenColumns)
  const tableHiddenColumnKey = getHiddenColumnKey(state.hiddenColumns || [])
  const lastPropHiddenColumnKey = useRef(null)
  const isApplyingExternalHiddenColumns = useRef(false)

  // Sync hidden columns after parent-driven updates.
  useEffect(() => {
    const propChanged = lastPropHiddenColumnKey.current !== hiddenColumnKey
    lastPropHiddenColumnKey.current = hiddenColumnKey

    if (propChanged && hiddenColumnKey !== tableHiddenColumnKey) {
      isApplyingExternalHiddenColumns.current = true
      setTableHiddenColumns(hiddenColumns)
    }
  }, [
    hiddenColumnKey,
    tableHiddenColumnKey,
    hiddenColumns,
    setTableHiddenColumns,
  ])

  // GLOBAL FILTER
  function GlobalFilter({ globalFilter, setGlobalFilter }) {
    return (
      <div className="inventory-search">
        <input
          value={globalFilter || ''}
          onChange={e => {
            setGlobalFilter(e.target.value)
          }}
          autoFocus
          placeholder="Buscar tienda por nombre o código"
        />
        <img className="inv-search-icon" src={Search} alt="Search icon" />
      </div>
    )
  }

  useEffect(() => {
    const handler = e => {
      if (!inventoryFilter?.current?.contains(e.target)) {
        setShowInventoryFilters(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
    }
  }, [])

  // Report the current visibility state back to the parent.
  useEffect(() => {
    if (isApplyingExternalHiddenColumns.current) {
      if (hiddenColumnKey === tableHiddenColumnKey) {
        isApplyingExternalHiddenColumns.current = false
      }
      return
    }

    const isDiscountVisible = toggleableColumns.some(
      column => column.ColumnName === 'Descuento' && column.Status
    )
    if (hiddenColumnDiscount) {
      hiddenColumnDiscount(isDiscountVisible)
    }
    setHiddenColumns(toggleableColumns)
  }, [
    hiddenColumnKey,
    tableHiddenColumnKey,
    toggleableColumns,
    hiddenColumnDiscount,
    setHiddenColumns,
  ])

  useEffect(() => {
    if (!isDetailQuote) {
      return
    }
    const showDiscount = toggleableColumns.some(
      col => col.ColumnName === 'Descuento' && col.Status
    )
    channel.postMessage({
      isDetailQuote,
      quoteData: useSelectorDetalle,
      showDiscount,
      isLoadingDetailQuote: false,
    })
  }, [toggleableColumns, isDetailQuote, channel, useSelectorDetalle])

  useEffect(() => {
    if (state.sortBy && handleSortChange) {
      handleSortChange && handleSortChange(state.sortBy)
    }
  }, [state.sortBy, handleSortChange])

  return (
    <>
      <div className="inventory-topbar">
        <div className="inventory-filter-options">
          <div>
            <button
              className="inventory-filter-button"
              onClick={() => setShowInventoryFilters(!showInventoryFilters)}>
              <Filter />
            </button>
            {showInventoryFilters && (
              <div ref={inventoryFilter} className="inventory-filters">
                {allColumns.map(column =>
                  column.hideEnabled &&
                  column.id !== 'Tienda' &&
                  column.id !== 'Código' &&
                  column.id !== 'Disponible' ? (
                    <label
                      id={column.id}
                      className="inventory-filter-option"
                      key={column.id}>
                      <input
                        id={column.id}
                        type="checkbox"
                        {...column.getToggleHiddenProps()}
                      />
                      {column.id}
                      {column.isVisible ? <Eye /> : <EyeSlash />}
                    </label>
                  ) : null
                )}
              </div>
            )}
          </div>
          {validateSalesOrder && (
            <button
              data-testid="btn-generate-sales-order"
              className={'inventory-sales-order-button ms-2'}
              onClick={generateSalesOrder}>
              {t('GenerateSalesOrder')}
            </button>
          )}

          {isDispatchGenerated &&
            !isPriceNotApproved &&
            isUserAllowed &&
            currentPath.includes('editar_cotizacion') && (
              <>
                <div className="switchContainer">
                  <CustomSwitch
                    data-testid="switch-free-taxes"
                    checked={freeTaxes}
                    onChange={handleSwitchChange}
                  />
                </div>
                <label className="textSwitchContainer">
                  {t('FreeTaxQuotations')}
                </label>
              </>
            )}
        </div>
        {isProductPage ? null : (
          <GlobalFilter
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {hasButtons ? topButtons : null}
      </div>
      <div className={`scrollable ${isInventory ? 'tableContainer' : ''}`}>
        <table className="inventory-table" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, indez) => (
                  <th
                    key={indez}
                    {...column.getHeaderProps({
                      ...(enableSorting && column.getSortByToggleProps
                        ? column.getSortByToggleProps()
                        : {}),
                      title: undefined,
                    })}>
                    <div className="order-header-table-container">
                      <span>{column.render('Header')}</span>
                      {enableSorting && !column.disableSortBy && (
                        <span
                          style={{
                            fontSize: '14px',
                            marginLeft: '9px',
                            transition: 'opacity 0.2s',
                            color: column.isSorted
                              ? 'var(--secondary-color)'
                              : 'var(--input-background-color)',
                          }}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ArrowDown />
                            ) : (
                              <ArrowUp />
                            )
                          ) : (
                            <ArrowDownUp />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, index) => {
              prepareRow(row)
              return (
                <tr key={index} {...row.getRowProps()}>
                  {row.cells.map((cell, indez) => {
                    return cell.column.Header === 'Margen' ? (
                      <td {...cell.getCellProps()}>
                        {showMargin ? cell.value : '****'}
                      </td>
                    ) : cell.column.Header === 'Imagen' ? (
                      <td
                        {...cell.getCellProps({
                          style: {
                            maxWidth: '200px',
                          },
                        })}>
                        <img
                          src={cell.value}
                          alt="product-image"
                          style={{ maxWidth: '50px' }}
                        />
                      </td>
                    ) : cell.column.Header === 'Precio Unitario' &&
                      !showDiscountOption ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }>
                        $ {Formatter.Money(cell.value)}
                      </td>
                    ) : cell.column.Header === 'Precio Unitario' ||
                      cell.column.Header === 'Descuento' ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }>
                        $ {Formatter.Money(cell.value)}
                      </td>
                    ) : cell.column.Header === 'Precio Total' ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }>
                        ${' '}
                        {Formatter.Money(cell.value, {
                          decimalPrecision: 2,
                          rounded: true,
                        })}
                      </td>
                    ) : cell.column.Header === 'Acciones' ? (
                      <td key={cell.value} {...cell.getCellProps()}>
                        <div className="inventory-actions">
                          <FontAwesomeIcon
                            icon={faSquarePen}
                            onClick={() => {
                              handleCartPopulation()
                            }}
                            style={{
                              display: hasButtons,
                              fontSize: '2.5rem',
                            }}
                          />
                          <FontAwesomeIcon
                            style={{
                              display: hasButtons,
                              color: '#F64E60',
                              fontSize: '2rem',
                            }}
                            icon={faTrashCan}
                            onClick={() => {
                              handleCartEdit(cell.row.original, cell.row.id)
                            }}
                          />
                        </div>
                      </td>
                    ) : (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {hideFooter ? null : (
            <tfoot
              className={isInventory ? 'stickyFooter' : null}
              style={{
                borderBottom: isProductPage && 'none',
                borderLeft: isProductPage && 'none',
                borderRight: isProductPage && 'none',
              }}>
              {footerGroups.map((group, index) => (
                <tr
                  key={index}
                  {...group.getFooterGroupProps()}
                  className="hidde-border-bottom">
                  {group.headers.map((column, indez) => {
                    return column.Header === 'Referencia' ? (
                      <td
                        key={indez}
                        style={{
                          border: isProductPage && 'none',
                          verticalAlign: 'top',
                          width: '18%',
                        }}
                        {...column.getFooterProps()}>
                        {column.render('Footer')}
                      </td>
                    ) : column.Header === 'Precio Unitario' ? (
                      !showDiscountOption ? (
                        <td
                          key={indez}
                          style={{
                            border: isProductPage && 'none',
                            verticalAlign: 'top',
                          }}
                          {...column.getFooterProps()}>
                          {column.render('Footer')}
                        </td>
                      ) : (
                        <td
                          key={indez}
                          style={{ border: isProductPage && 'none' }}></td>
                      )
                    ) : (
                      <td
                        key={indez}
                        style={{ border: isProductPage && 'none' }}
                        {...column.getFooterProps()}>
                        {column.render('Footer')}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </>
  )
}
export default FilteringTable
